import { Container, Graphics, Text } from "pixi.js";

export class LetterTile extends Container {
  private bg: Graphics;
  private letterText: Text;
  private valueText: Text;
  private premiumTag: Text | null = null;
  private originalColor = 0xf5deb3;
  private isRevealed = false;

  constructor(
    public letter: string,
    public value: number,
    cellSize: number,
    public premiumType: string | null = null
  ) {
    super();
    const size = cellSize - 2;

    this.bg = new Graphics();
    this.bg.roundRect(1, 1, size, size, 4);
    this.bg.fill({ color: this.originalColor });
    this.bg.stroke({ width: 1, color: 0x8b7355 });
    this.addChild(this.bg);

    this.letterText = new Text({
      text: letter,
      style: { fontSize: 18, fill: 0x000000, fontFamily: "monospace", fontWeight: "bold" },
    });
    this.letterText.anchor.set(0.5);
    this.letterText.x = cellSize / 2;
    this.letterText.y = cellSize / 2 - 4;
    this.addChild(this.letterText);

    this.valueText = new Text({
      text: value > 0 ? String(value) : "",
      style: { fontSize: 10, fill: 0x666666, fontFamily: "monospace" },
    });
    this.valueText.anchor.set(0.5);
    this.valueText.x = cellSize / 2 + 10;
    this.valueText.y = cellSize / 2 + 8;
    this.addChild(this.valueText);

    // Premium square tag so the letter tile keeps showing DL/TL/DW/TW
    if (premiumType) {
      this.premiumTag = new Text({
        text: premiumType,
        style: { fontSize: 7, fill: 0x7a2e0d, fontFamily: "monospace", fontWeight: "bold" },
      });
      this.premiumTag.anchor.set(1, 0);
      this.premiumTag.x = cellSize - 2;
      this.premiumTag.y = 1;
      this.addChild(this.premiumTag);
    }

    this.eventMode = "static";
    this.cursor = "pointer";
  }

  async popIn() {
    this.scale.set(0.3);
    const duration = 250;
    const start = performance.now();
    return new Promise<void>((resolve) => {
      const animate = (now: number) => {
        const t = Math.min((now - start) / duration, 1);
        // Elastic overshoot: goes past 1 then settles
        const elastic = t === 0 ? 0 : t === 1 ? 1 :
          Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * (2 * Math.PI) / 3) + 1;
        this.scale.set(0.3 + elastic * 0.7);
        if (t < 1) {
          requestAnimationFrame(animate);
        } else {
          this.scale.set(1);
          resolve();
        }
      };
      requestAnimationFrame(animate);
    });
  }

  async popOut() {
    const duration = 200;
    const start = performance.now();
    return new Promise<void>((resolve) => {
      const animate = (now: number) => {
        const t = Math.min((now - start) / duration, 1);
        const ease = 1 - Math.pow(1 - t, 3);
        this.scale.set(1 - ease * 0.7);
        this.alpha = 1 - ease;
        if (t < 1) {
          requestAnimationFrame(animate);
        } else {
          this.scale.set(0.3);
          this.alpha = 0;
          resolve();
        }
      };
      requestAnimationFrame(animate);
    });
  }

  async flipReveal(correct: boolean) {
    if (this.isRevealed) return;
    this.isRevealed = true;

    const duration = 400;
    const half = duration / 2;
    const start = performance.now();
    const targetColor = correct ? 0x86efac : 0xfca5a5; // green-300 or red-300
    const targetStroke = correct ? 0x22c55e : 0xef4444;

    return new Promise<void>((resolve) => {
      const animate = (now: number) => {
        const elapsed = now - start;
        if (elapsed < half) {
          // First half: scale down to flat
          const t = elapsed / half;
          const ease = 1 - Math.pow(1 - t, 2);
          this.scale.x = 1 - ease;
        } else if (elapsed < duration) {
          // Second half: scale back up with new color
          const t = (elapsed - half) / half;
          const ease = 1 - Math.pow(1 - t, 2);
          this.scale.x = ease;

          // Change color at the midpoint
          if (elapsed >= half && elapsed < half + 20) {
            this.bg.clear();
            const size = this.bg.width || 38; // fallback
            this.bg.roundRect(1, 1, size, size, 4);
            this.bg.fill({ color: targetColor });
            this.bg.stroke({ width: 2, color: targetStroke });
          }
        } else {
          this.scale.x = 1;
          resolve();
          return;
        }
        requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    });
  }

  pulse() {
    const duration = 600;
    const start = performance.now();
    const animate = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const wave = Math.sin(t * Math.PI * 4) * 0.1;
      this.scale.set(1 + wave);
      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        this.scale.set(1);
      }
    };
    requestAnimationFrame(animate);
  }

  setDragging(dragging: boolean) {
    this.alpha = dragging ? 0.7 : 1;
  }

  resetAppearance() {
    this.isRevealed = false;
    this.alpha = 1;
    this.scale.set(1);
    this.bg.clear();
    const size = 38;
    this.bg.roundRect(1, 1, size, size, 4);
    this.bg.fill({ color: this.originalColor });
    this.bg.stroke({ width: 1, color: 0x8b7355 });
  }
}
